import React from "react";
import { Box, Button, Switch, FormControlLabel } from "@material-ui/core";
import { CTextField, CSelect, HFlex, ProgressButton, Text, useErrorContext } from "shared";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Save } from "@material-ui/icons";
import { UpdateHcpInput, useUpdateHcp } from "api/hooks/useUpdateHcp";
import { useHealthCareOrganizations } from "routes/healthCareOrganizations/queries/useHealthCareOrganizations";
import { Hcp } from "../types";

interface Props {
  hcp: Hcp;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  phone_number: string;
  email: string;
  address: string;
  health_care_organization_id: number | "";
  admits_patients: boolean;
}

export const EditHcpForm: React.VFC<Props> = ({ hcp, onSuccess, onCancel }) => {
  const form = useForm<FormData>({
    defaultValues: {
      name: hcp.name || "",
      phone_number: hcp.phone_number || "",
      email: hcp.email || "",
      address: hcp.address || "",
      health_care_organization_id: hcp.health_care_organization_id || "",
      admits_patients: hcp.admits_patients || false,
    },
  });
  const { t } = useTranslation();
  const setError = useErrorContext();
  const { data: hcos } = useHealthCareOrganizations();

  const mappedHCOs = [
    { value: "" as const, label: t("form.none") },
    ...(hcos
      ?.map(hco => ({ value: hco.id, label: hco.name }))
      ?.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())) || []),
  ];

  const updateMutation = useUpdateHcp();

  const submit = form.handleSubmit(async fields => {
    try {
      const updateData: Partial<Omit<UpdateHcpInput, "hcpId">> & Pick<UpdateHcpInput, "hcpId"> = {
        hcpId: hcp.id,
      };

      // Only include fields that have changed
      if (fields.name !== hcp.name) updateData.name = fields.name;
      if (fields.phone_number !== hcp.phone_number) updateData.phone_number = fields.phone_number;
      if (fields.email !== hcp.email) updateData.email = fields.email;
      if (fields.address !== hcp.address) updateData.address = fields.address;
      if (fields.admits_patients !== hcp.admits_patients) updateData.admits_patients = fields.admits_patients;

      // Handle health care organization - empty string means set to null
      if (fields.health_care_organization_id === "") {
        updateData.health_care_organization_id = null;
      } else if (fields.health_care_organization_id !== hcp.health_care_organization_id) {
        updateData.health_care_organization_id = fields.health_care_organization_id;
      }

      await updateMutation.mutateAsync(updateData);
      onSuccess();
    } catch (err) {
      setError(t("errors.could_not_update_hcp"), err);
    }
  });

  return (
    <Box mx={3}>
      <Text heading bold mb={3}>
        {t("form.edit_health_care_provider")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField required name="name" autoFocus label={t("form.name")} margin="normal" size="small" fullWidth />

          <CTextField name="phone_number" label={t("form.phone")} margin="normal" size="small" fullWidth />

          <CTextField name="email" label={t("form.email")} type="email" margin="normal" size="small" fullWidth />

          <CTextField name="address" label={t("form.address")} margin="normal" size="small" fullWidth />

          <CSelect
            name="health_care_organization_id"
            label={t("form.health_care_organization")}
            items={mappedHCOs}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <Controller
            name="admits_patients"
            control={form.control}
            render={({ value, onChange }) => (
              <FormControlLabel
                control={<Switch color="primary" checked={value} onChange={e => onChange(e.target.checked)} />}
                label={<Text>{t("form.admits_patients")}</Text>}
              />
            )}
          />

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button variant="outlined" onClick={onCancel} disabled={updateMutation.isLoading}>
              {t("buttons.cancel")}
            </Button>
            <ProgressButton loading={updateMutation.isLoading} endIcon={<Save />}>
              {t("buttons.save")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};
